import React, { useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import { GuaranteeOption } from "../../../NewUpsell/Components/GuaranteeToggles/GuaranteeToggles";
import V3PriceDisplay from "./V3PriceDisplay";

interface V3CoverageCardsProps {
  translate: (key: string) => string;
  guarantees: GuaranteeOption[];
  onToggle: (key: string) => void;
  onCapitalChange: (key: string, capital: number) => void;
  totalAmount: number;
  loading: boolean;
  beforePromoText?: string;
  afterPromoText?: string;
  onPromoClick?: () => void;
  showDisclaimers?: boolean;
}

const formatCapital = (value: number): string => {
  return new Intl.NumberFormat(
    process.env.REACT_APP_DOMAIN_LOCALE === "fr" ? "fr-FR" : "es-ES"
  ).format(value);
};

const TRAIT_ICON_MAP: Record<string, string> = {
  DEATH: "ShieldCheckIcon",
  DISABILITY: "WheelChairIcon",
  SERIOUS_ILLNESS: "HealthIcon",
  MALATTIA_TERMINALE: "ShieldCheckIcon",
};

const V3CoverageCards: React.FC<V3CoverageCardsProps> = ({
  translate,
  guarantees,
  onToggle,
  onCapitalChange,
  totalAmount,
  loading,
  beforePromoText,
  afterPromoText,
  onPromoClick,
  showDisclaimers,
}) => {
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());

  const toggleEditing = (key: string) => {
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (guarantees.length === 0) return null;
  const activeCount = guarantees.filter((g) => g.active).length;

  return (
    <section className="upsell-v3__coverages">
      <div className="upsell-v3__coverages-inner">
        <div className="upsell-v3__coverages-header">
          <div>
            <div className="upsell-v3__eyebrow">
              {translate("newUpsell.v3.coverages.eyebrow")}
            </div>
            <h2 className="upsell-v3__coverages-title">
              {translate("newUpsell.v3.coverages.title")}
              <br />
              {translate("newUpsell.v3.coverages.titleLine2")}
            </h2>
          </div>
          <div className="upsell-v3__coverages-status">
            <div className="label">
              {loading
                ? translate("newUpsell.v3.coverages.recalculating")
                : translate("newUpsell.v3.coverages.activeCount")
                    .replace("{active}", String(activeCount))
                    .replace("{total}", String(guarantees.length))}
            </div>
            {beforePromoText && (
              <p
                className="upsell-v3__coverages-before"
                dangerouslySetInnerHTML={{ __html: beforePromoText }}
              />
            )}
            <V3PriceDisplay amount={totalAmount} loading={loading} size={36} suffix="/mes" />
            {afterPromoText && (
              <p className="upsell-v3__coverages-after">
                <span dangerouslySetInnerHTML={{ __html: afterPromoText }} />
                {onPromoClick && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={onPromoClick}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        onPromoClick();
                      }
                    }}
                    className="upsell-v3__coverages-after-info"
                  >
                    <GlobalIcon iconName="InfoIcon" size="XS" color="#7B7B7B" />
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="upsell-v3__coverages-grid">
          {guarantees.map((g) => {
            const active = g.active;
            const iconName = TRAIT_ICON_MAP[g.key] ?? g.icon ?? "ShieldCheckIcon";
            const iconColor = active ? "white" : "#423EF4";
            const isMandatory = !g.enabled;
            const showCapital = active && g.capital !== undefined;

            return (
              <div
                key={g.key}
                className={`upsell-v3__coverages-card${active ? " upsell-v3__coverages-card--active" : ""}${g.loading ? " upsell-v3__coverages-card--loading" : ""}`}
              >
                <div className="upsell-v3__coverages-cardTop">
                  <div
                    className={`upsell-v3__coverages-icon${active ? " upsell-v3__coverages-icon--active" : ""}`}
                  >
                    <GlobalIcon iconName={iconName as any} size="S" color={iconColor} />
                  </div>
                  <button
                    type="button"
                    className={`upsell-v3__coverages-toggle${active ? " upsell-v3__coverages-toggle--on" : ""}`}
                    onClick={() => g.enabled && !g.loading && onToggle(g.key)}
                    disabled={!g.enabled || g.loading}
                    role="switch"
                    aria-checked={active}
                    aria-label={`${active ? translate("upsell.guarantees.remove") : translate("upsell.guarantees.add")} ${g.label}`}
                  >
                    <span className="upsell-v3__coverages-toggle-slider" />
                  </button>
                </div>
                <div>
                  <div className="upsell-v3__coverages-cardName">
                    {g.label}
                    {isMandatory && (
                      <span className="upsell-v3__coverages-cardBaseTag">
                        {translate("newUpsell.v3.coverages.baseLabel")}
                      </span>
                    )}
                  </div>
                  <div className="upsell-v3__coverages-cardDesc">
                    {translate(`newUpsell.v3.coverages.shortDesc.${g.key}`) ||
                      g.label}
                  </div>
                </div>

                {showCapital && (
                  <div className="upsell-v3__coverages-capital">
                    <div className="upsell-v3__coverages-capital-row">
                      <span className="upsell-v3__coverages-capital-label">
                        {translate("newUpsell.v3.coverages.capitalLabel")}
                      </span>
                      <span className="upsell-v3__coverages-capital-value">
                        {formatCapital(g.capital ?? 0)} €
                      </span>
                      {g.editableCapital !== false && (
                        <button
                          type="button"
                          className="upsell-v3__coverages-capital-edit"
                          onClick={() => toggleEditing(g.key)}
                          disabled={g.loading}
                          aria-expanded={editingKeys.has(g.key)}
                          aria-controls={`capital-slider-${g.key}`}
                        >
                          {editingKeys.has(g.key)
                            ? translate("newUpsell.v3.coverages.doneCapital")
                            : translate("newUpsell.v3.coverages.editCapital")}
                        </button>
                      )}
                    </div>
                    {g.editableCapital !== false && editingKeys.has(g.key) && (
                      <div
                        id={`capital-slider-${g.key}`}
                        className="upsell-v3__coverages-capital-editor"
                      >
                        <input
                          type="range"
                          className="upsell-v3__coverages-capital-slider"
                          min={g.minCapital ?? 0}
                          max={g.maxCapital ?? 0}
                          step={5000}
                          value={g.capital ?? 0}
                          onChange={(e) =>
                            onCapitalChange(g.key, parseInt(e.target.value, 10))
                          }
                          disabled={g.loading}
                          aria-label={`${translate("newUpsell.v3.coverages.capitalLabel")} ${g.label}`}
                          style={{
                            background: (() => {
                              const min = g.minCapital ?? 0;
                              const max = g.maxCapital ?? 0;
                              const cap = g.capital ?? 0;
                              const pct =
                                max > min ? ((cap - min) / (max - min)) * 100 : 0;
                              return `linear-gradient(to right, #423EF4 0%, #423EF4 ${pct}%, #ECF0FF ${pct}%, #ECF0FF 100%)`;
                            })(),
                          }}
                        />
                        <div className="upsell-v3__coverages-capital-range">
                          <span>{formatCapital(g.minCapital ?? 0)} €</span>
                          <span>{formatCapital(g.maxCapital ?? 0)} €</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {showDisclaimers && (
          <div className="upsell-v3__coverages-disclaimers">
            {(["deathDisability", "seriousIllness"] as const).map((k) => (
              <p className="upsell-v3__coverages-disclaimer" key={k}>
                <strong>
                  {translate(`newUpsell.v3.coverages.disclaimer.${k}.title`)}
                </strong>{" "}
                {translate(`newUpsell.v3.coverages.disclaimer.${k}.text`)}
              </p>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default V3CoverageCards;
